import React, { forwardRef, useState, useEffect, useRef } from 'react';
import { X, Search, Loader2 } from 'lucide-react';
import PropTypes from 'prop-types';

/**
 * A specialized Search Input component that provides:
 * - A search icon (left) or spinner (when loading/debouncing).
 * - A clear button (right) when value exists.
 * - Auto-trim functionality on blur.
 * - Standard styling.
 * - Built-in debouncing (default 300ms) to reduce excessive parent updates/API calls.
 *
 * Use this component for all search/filtering text fields.
 *
 * @param {string} value - The current value of the input.
 * @param {function} onChange - Callback function when the value changes.
 * @param {string} className - Additional CSS classes.
 * @param {boolean} trimOnBlur - Whether to automatically trim whitespace on blur. Default is true.
 * @param {number} debounceDelay - Delay in milliseconds for debouncing onChange. Default is 300.
 * @param {boolean} loading - Whether to show a loading spinner. Default is false.
 * @param {object} props - Additional props passed to the standard input element.
 */
const SearchInput = forwardRef(({
    value,
    onChange,
    className = '',
    trimOnBlur = true,
    placeholder = 'Buscar...',
    onKeyDown,
    debounceDelay = 300,
    loading = false,
    ...props
}, ref) => {
    // Local state to manage the input value for immediate UI feedback
    const [localValue, setLocalValue] = useState(value);
    const [isDebouncing, setIsDebouncing] = useState(false);
    const timeoutRef = useRef(null);

    // Sync local state when external value changes
    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, []);

    const handleChange = (e) => {
        const newValue = e.target.value;
        setLocalValue(newValue);
        setIsDebouncing(true);

        // If debounceDelay is 0, call immediately
        if (debounceDelay === 0) {
            setIsDebouncing(false);
            if (onChange) onChange(e);
            return;
        }

        // Clear existing timeout
        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        // Construct synthetic event data to use inside timeout
        const eventName = e.target.name;

        timeoutRef.current = setTimeout(() => {
            setIsDebouncing(false);
            if (onChange) {
                // Create a synthetic event
                const syntheticEvent = {
                    target: {
                        value: newValue,
                        name: eventName || props.name || '',
                        type: 'text'
                    }
                };
                onChange(syntheticEvent);
            }
        }, debounceDelay);
    };

    const handleClear = (e) => {
        e.stopPropagation();
        e.preventDefault();

        // Cancel any pending debounced change
        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        setIsDebouncing(false);

        // Update local state immediately
        setLocalValue('');

        // Call onChange immediately
        const syntheticEvent = {
            target: {
                value: '',
                name: props.name || '',
                type: 'text'
            }
        };

        if (onChange) {
            onChange(syntheticEvent);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape' && localValue) {
            e.preventDefault();
            e.stopPropagation();
            handleClear(e);
        }

        if (onKeyDown) {
            onKeyDown(e);
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const clipboardData = e.clipboardData || window.clipboardData;
        const pastedData = clipboardData.getData('text');
        const trimmedData = pastedData.trim();

        const input = e.target;
        const start = input.selectionStart || 0;
        const end = input.selectionEnd || 0;
        const currentVal = localValue || '';

        const newValue = currentVal.substring(0, start) + trimmedData + currentVal.substring(end);

        // Create a synthetic event for handleChange
        const syntheticEvent = {
            target: {
                value: newValue,
                name: input.name,
                type: 'text'
            }
        };

        handleChange(syntheticEvent);
    };

    const handleBlur = (e) => {
        // Use localValue for trimming logic
        if (trimOnBlur && localValue && typeof localValue === 'string') {
            const trimmedValue = localValue.trim();
            if (trimmedValue !== localValue) {
                setLocalValue(trimmedValue); // Update local visual

                // Trigger immediate change for the trimmed value on blur
                if (timeoutRef.current) clearTimeout(timeoutRef.current);
                setIsDebouncing(false);

                const syntheticEvent = {
                    target: {
                        value: trimmedValue,
                        name: props.name || '',
                        type: 'text'
                    }
                };
                if (onChange) {
                    onChange(syntheticEvent);
                }
            }
        }

        if (props.onBlur) {
            props.onBlur(e);
        }
    };

    const handleFocus = (e) => {
        if (props.onFocus) {
            props.onFocus(e);
        }
    };

    // Use localValue for visibility check to ensure instant feedback
    const showClearButton = !props.disabled && !props.readOnly && localValue && String(localValue).length > 0;
    const isLoading = loading || isDebouncing;

    return (
        <div className="relative w-full" aria-busy={isLoading}>
            {/* Search Icon or Loading Spinner */}
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 pointer-events-none">
                {isLoading ? (
                    <Loader2 size={18} className="animate-spin text-blue-500" aria-label="Carregando" />
                ) : (
                    <Search size={18} />
                )}
            </div>

            <input
                ref={ref}
                type="text"
                value={localValue !== undefined && localValue !== null ? localValue : ''}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={handleFocus}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                placeholder={placeholder}
                className={`input-style w-full pl-10 pr-10 text-left ${className}`}
                dir="ltr"
                {...props}
            />

            {/* Clear Button */}
            {showClearButton && (
                <button
                    type="button"
                    onClick={handleClear}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                    aria-label="Limpar busca"
                    title="Limpar busca"
                >
                    <X size={16} />
                </button>
            )}
        </div>
    );
});

SearchInput.displayName = 'SearchInput';

SearchInput.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func,
    className: PropTypes.string,
    trimOnBlur: PropTypes.bool,
    placeholder: PropTypes.string,
    debounceDelay: PropTypes.number,
    loading: PropTypes.bool,
};

export default SearchInput;
